import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "~/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
        "floating-error":
          "bg-[#EF4444] text-white border-black/5 rounded-xl px-3 py-2 shadow-lg flex-row items-center",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface AlertProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof alertVariants> {
  autoDismiss?: number // Duration in milliseconds, defaults to 3000 (3 seconds)
  onDismiss?: () => void
}

function Alert({
  className,
  variant,
  autoDismiss,
  onDismiss,
  ...props
}: AlertProps) {
  const [isVisible, setIsVisible] = React.useState(true)

  React.useEffect(() => {
    if (autoDismiss && autoDismiss > 0 && onDismiss) {
      const timer = setTimeout(() => {
        setIsVisible(false)
        onDismiss()
      }, autoDismiss)

      return () => clearTimeout(timer)
    }
  }, [autoDismiss, onDismiss])

  if (!isVisible) {
    return null
  }

  return (
    <div
      data-slot="alert"
      data-variant={variant}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed data-[variant=floating-error]:col-start-1 data-[variant=floating-error]:text-white data-[variant=floating-error]:font-semibold data-[variant=floating-error]:leading-5",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
